/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {useQuery} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {loadAllDevicesUsingSmartSearch} from '@ion/core/src/services/Connect';
import {LoadParams} from '@ion/core/src/types/services';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, DurationResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseGetDevicesResult = UseDefaultQueryResult & {
  results: any[];
  refresh: Function;
  isLoading: boolean;
  isError: boolean;
};

export interface UseGetDevicesProps {
  baseKey?: QueryKey;
  enabled?: boolean;
  staleTime?: number | DurationResult;
  queryParams?: LoadParams;
};

/**
 * The useGetDevices hook provides the Query for loading devices using parallel APIs.
 *
 * ```typescript
 *
 * import {useGetDevices} from '@ion/query/src/hooks/';
 *
 *  ...
 *
 *  const {results} = useGetDevices();
 *
 * ```
 *
 * @param   baseKey a constant passed in to identify the dependency key.
 * @param   enabled when true, the query will be executed immediately. Defaults to
 *          false in order to allow for updates to occur only at the user's discretion.
 * @param   queryParams params digestible by the API request.
 * @returns UseGetDevicesResult containing the query results.
 */
export const useGetDevices = ({
  queryParams,
  enabled = false,
  baseKey = QueryKey.GET_ALL_DEVICES,
  staleTime = getStaleTime(5, false, false)
}: UseGetDevicesProps): UseGetDevicesResult => {
  const key = useMemo(() => (
    createKey(
      baseKey,
      queryParams
    )
  ), [
    baseKey,
    queryParams
  ]);

  const query = useQuery(key, async() => {
    try {
      const {
        accountId,
        queryString,
        customQuery,
        includeSubGroups
      } = queryParams;
      const results = await loadAllDevicesUsingSmartSearch({
        accountId,
        pageId   : 1,
        pageSize : 100,
        usePulse : true,
        includeSubGroups,
        queryString,
        customQuery
      }, true);
      return results || [];
    } catch (err) {
      error(err);
      return [];
    }
  }, {
    staleTime : staleTime as number,
    enabled
  });

  return createQueryResult(query, {
    results   : query.data,
    isLoading : query.isLoading
  }) as UseGetDevicesResult;
};

export default useGetDevices;
